import { LangageIso6391 } from "@tfg/db"
import type { TextContent, TextTranslation } from "@tfg/db"
import { CountryFlag } from "@tfg/ui"
import { FlagComponent } from "country-flag-icons/react/1x1"
import { ReactElement, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { LanguageIso6391 } from "@tfg/types/constants/language.constants"

import { i18n } from "~/i18n"

export type LanguageInfo = {
  languageName: string
  originalLanguageName: string
  flag: ReactElement<FlagComponent>
}
export function useLocalizedTextByTextContent() {
  const { i18n } = useTranslation()

  return (
    textContent: TextContent & {
      TextTranslation: TextTranslation[]
    }
  ) => {
    const currentLanguage = i18n.language

    const translation = textContent.TextTranslation.find(
      (translation) =>
        translation.translatedLanguage === currentLanguage.toUpperCase()
    )

    return translation ? translation.translation : textContent.originalText
  }
}
export const LANGUAGES: {
  [language in LangageIso6391]: LanguageInfo
} = {
  FR: {
    languageName: i18n.t("languages.french"),
    originalLanguageName: "Français",
    flag: <CountryFlag countryIso3166="FR" />,
  },
  EN: {
    languageName: i18n.t("languages.english"),
    originalLanguageName: "English",
    flag: <CountryFlag countryIso3166="GB" />,
  },
  ZH: {
    languageName: i18n.t("languages.chinese"),
    originalLanguageName: "中文",
    flag: <CountryFlag countryIso3166="CN" />,
  },
  PT: {
    languageName: i18n.t("languages.portuguese"),
    originalLanguageName: "Português",
    flag: <CountryFlag countryIso3166="PT" />,
  },
  IT: {
    languageName: i18n.t("languages.italian"),
    originalLanguageName: "Italiano",
    flag: <CountryFlag countryIso3166="IT" />,
  },
  ES: {
    languageName: i18n.t("languages.spanish"),
    originalLanguageName: "Español",
    flag: <CountryFlag countryIso3166="ES" />,
  },
  TR: {
    languageName: i18n.t("languages.turkish"),
    originalLanguageName: "Türkçe",
    flag: <CountryFlag countryIso3166="TR" />,
  },
  NL: {
    languageName: i18n.t("languages.dutch"),
    originalLanguageName: "Nederlands",
    flag: <CountryFlag countryIso3166="NL" />,
  },
  DE: {
    languageName: i18n.t("languages.german"),
    originalLanguageName: "Deutsch",
    flag: <CountryFlag countryIso3166="DE" />,
  },
  AR: {
    languageName: i18n.t("languages.arabic"),
    originalLanguageName: "Arabic",
    flag: <CountryFlag countryIso3166="SA" />,
  },
  KO: {
    languageName: i18n.t("languages.korean"),
    originalLanguageName: "한국어",
    flag: <CountryFlag countryIso3166="KR" />,
  },
  JA: {
    languageName: i18n.t("languages.japanese"),
    originalLanguageName: "日本語",
    flag: <CountryFlag countryIso3166="JP" />,
  },
  BE: {
    languageName: i18n.t("languages.belarusian"),
    originalLanguageName: "Беларуская",
    flag: <CountryFlag countryIso3166="BY" />,
  },
  RO: {
    languageName: i18n.t("languages.romanian"),
    originalLanguageName: "Română",
    flag: <CountryFlag countryIso3166="RO" />,
  },
  LT: {
    languageName: i18n.t("languages.lithuanian"),
    originalLanguageName: "Lietuvių",
    flag: <CountryFlag countryIso3166="LT" />,
  },
} as const

export const findLanguage = (locale: string): string => {
  const language = LANGUAGES[locale as keyof typeof LANGUAGES] || LANGUAGES.EN
  return language.originalLanguageName
}

export const findLanguageFlag = (
  locale: LanguageIso6391
): ReactElement<FlagComponent> => {
  const language = LANGUAGES[locale as keyof typeof LANGUAGES] || LANGUAGES.EN

  return language.flag
}

export const useLanguageNames = (lng?: string) => {
  const { t } = useTranslation("common")

  const options = useMemo(() => (lng ? { lng } : undefined), [lng])

  const languageNames = useMemo<{
    [language in LangageIso6391]: string
  }>(
    () =>
      ({
        FR: t("languages.french", options),
        EN: t("languages.english", options),
        ZH: t("languages.chinese", options),
        PT: t("languages.portuguese", options),
        IT: t("languages.italian", options),
        ES: t("languages.spanish", options),
        TR: t("languages.turkish", options),
        NL: t("languages.dutch", options),
        DE: t("languages.german", options),
        AR: t("languages.arabic", options),
        KO: t("languages.korean", options),
        JA: t("languages.japanese", options),
        BE: t("languages.belarusian", options),
        RO: t("languages.romanian", options),
        LT: t("languages.lithuanian", options),
      }) as const,
    [t, options]
  )

  const getLanguageName = useCallback(
    (locale: LangageIso6391) => {
      return languageNames[locale]
    },
    [languageNames]
  )

  return {
    languageNames,
    getLanguageName,
  }
}
